import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMyTimesheetEntries, addTimesheetEntry, updateTimesheetEntry, deleteTimesheetEntry,
  getTeamTimesheetEntries, getEmployees,
  getMyWeekStatus, submitTimesheetWeek, getTimesheetApprovals,
  approveTimesheetWeek, rejectTimesheetWeek,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { LoadingOverlay } from "../components/LoadingOverlay";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function exportCsv(rows: any[], filename: string) {
  const cols = ["date", "employee_name", "project_name", "client_name", "hours", "notes"];
  const header = ["Date", "Employee", "Project", "Client", "Hours", "Notes"];
  const lines = [
    header.join(","),
    ...rows.map(r => cols.map(c => {
      const v = r[c] ?? "";
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(",")),
  ];
  // Leading BOM (bug-17) — without it, Excel opens this UTF-8 file as Windows-1252 and
  // renders any non-ASCII character as mojibake.
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtWeek(start: Date): string {
  const end = addDays(start, 6);
  return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

// ── Add entry inline form ──────────────────────────────────────────────────────
function AddEntryRow({ date, onSave }: { date: string; onSave: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ project_name: "", client_name: "", hours: "", notes: "" });
  const [open, setOpen] = useState(false);

  const mut = useMutation({
    mutationFn: () => addTimesheetEntry({ ...form, date, hours: parseFloat(form.hours) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheet-my"] });
      setForm({ project_name: "", client_name: "", hours: "", notes: "" });
      setOpen(false);
      onSave();
      toast("Entry added", "success");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ fontSize: 12, color: NAVY, background: "none", border: `1px dashed ${NAVY}`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontWeight: 600 }}>
        + Add
      </button>
    );
  }

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
      <LoadingOverlay active={mut.isPending} />
      <input value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
        placeholder="Project *" style={{ ...inSm, flex: "2 1 110px" }} />
      <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
        placeholder="Client" style={{ ...inSm, flex: "1 1 80px" }} />
      <input type="number" min="0.5" max="24" step="0.5" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
        placeholder="Hrs" style={{ ...inSm, width: 56 }} />
      <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
        placeholder="Notes" style={{ ...inSm, flex: "2 1 100px" }} />
      <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.project_name || !form.hours}
        style={{ ...smBtn, background: NAVY, color: "white" }}>{mut.isPending ? "…" : "Save"}</button>
      <button onClick={() => setOpen(false)} style={smBtn}>✕</button>
    </div>
  );
}

// ── Entry chip (existing entry) ───────────────────────────────────────────────
const EntryChip: React.FC<{ entry: any; onDelete: () => void; onEdit: (e: any) => void; deleteDisabled?: boolean }> = ({ entry, onDelete, onEdit, deleteDisabled }) => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 8px", fontSize: 12, flexWrap: "wrap" }}>
      <span style={{ fontWeight: 700, color: NAVY }}>{entry.hours}h</span>
      <span style={{ fontWeight: 600, color: "#1e293b" }}>{entry.project_name}</span>
      {entry.client_name && <span style={{ color: "#64748b" }}>· {entry.client_name}</span>}
      {entry.notes && <span style={{ color: "#94a3b8" }}>— {entry.notes}</span>}
      <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
        <button onClick={() => onEdit(entry)} style={{ ...smBtn, padding: "1px 6px", fontSize: 11 }}>Edit</button>
        <button onClick={onDelete} disabled={deleteDisabled} style={{ ...smBtn, padding: "1px 6px", fontSize: 11, color: "#dc2626" }}>×</button>
      </div>
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────
function EditModal({ entry, onClose }: { entry: any; onClose: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ project_name: entry.project_name, client_name: entry.client_name || "", hours: String(entry.hours), notes: entry.notes || "" });

  const mut = useMutation({
    mutationFn: () => updateTimesheetEntry(entry.id, { ...form, hours: parseFloat(form.hours) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["timesheet-my"] }); onClose(); toast("Updated", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <LoadingOverlay active={mut.isPending} />
      <div style={{ background: "white", borderRadius: 12, padding: 28, width: 420, maxWidth: "92vw", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 18 }}>Edit Entry</div>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={lbl}>Project *</label>
            <input value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} style={inFull} />
          </div>
          <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Client</label>
              <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} style={inFull} />
            </div>
            <div>
              <label style={lbl}>Hours</label>
              <input type="number" min="0.5" max="24" step="0.5" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} style={inFull} />
            </div>
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={inFull} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.project_name || !form.hours}
            style={{ background: NAVY, color: "white", border: "none", padding: "9px 22px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
            {mut.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} style={{ background: "white", border: "1px solid #e2e8f0", padding: "9px 16px", borderRadius: 7, cursor: "pointer", fontSize: 14, color: "#64748b" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── My Timesheet tab ──────────────────────────────────────────────────────────
function MyTimesheetTab({ weekStart, weekLabel }: { weekStart: Date; weekLabel: string }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editEntry, setEditEntry] = useState<any>(null);
  const [submitNotes, setSubmitNotes] = useState("");
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const isContractor = user?.role === "contractor";

  const weekDates = DAYS.map((_, i) => toIso(addDays(weekStart, i)));
  const startStr = weekDates[0];
  const endStr = weekDates[6];

  const { data: entries = [], isLoading } = useQuery<any[]>({
    queryKey: ["timesheet-my", startStr, endStr],
    queryFn: () => getMyTimesheetEntries({ start_date: startStr, end_date: endStr }),
  });

  const { data: weekStatus } = useQuery({
    queryKey: ["timesheet-week-status", startStr],
    queryFn: () => getMyWeekStatus(startStr),
    enabled: isContractor,
  });

  const submitMut = useMutation({
    mutationFn: () => submitTimesheetWeek(startStr, submitNotes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheet-week-status"] });
      setShowSubmitForm(false);
      setSubmitNotes("");
      toast("Week submitted for approval", "success");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to submit", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteTimesheetEntry(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["timesheet-my"] }); toast("Deleted", "success"); },
    onError: () => toast("Failed to delete", "error"),
  });

  const weekLocked = isContractor && weekStatus?.status && ["submitted", "approved"].includes(weekStatus.status);

  const byDate: Record<string, any[]> = {};
  weekDates.forEach(d => { byDate[d] = []; });
  entries.forEach((e: any) => { if (byDate[e.date]) byDate[e.date].push(e); });

  const totalHours = entries.reduce((s: number, e: any) => s + (e.hours || 0), 0);

  if (isLoading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>;

  return (
    <>
      <LoadingOverlay active={submitMut.isPending || deleteMut.isPending} />
      {editEntry && <EditModal entry={editEntry} onClose={() => setEditEntry(null)} />}

      {/* Contractor week status banner */}
      {isContractor && weekStatus?.status && weekStatus.status !== "draft" && (
        <div style={{
          borderRadius: 10, padding: "12px 18px", marginBottom: 16,
          background: weekStatus.status === "approved" ? "#f0fdf4" : weekStatus.status === "submitted" ? "#fefce8" : "#fef2f2",
          border: `1px solid ${weekStatus.status === "approved" ? "#bbf7d0" : weekStatus.status === "submitted" ? "#fde68a" : "#fecaca"}`,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 18 }}>{weekStatus.status === "approved" ? "✅" : weekStatus.status === "submitted" ? "⏳" : "↩️"}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
              Week {weekStatus.status === "approved" ? "Approved" : weekStatus.status === "submitted" ? "Pending Approval" : "Returned for Revision"}
            </div>
            {weekStatus.status === "rejected" && weekStatus.rejection_reason && (
              <div style={{ fontSize: 12, color: "#dc2626", marginTop: 2 }}>Reason: {weekStatus.rejection_reason}</div>
            )}
          </div>
        </div>
      )}

      {totalHours > 0 && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 20 }}>
          <div>
            <span style={{ fontSize: 24, fontWeight: 800, color: NAVY }}>{totalHours}</span>
            <span style={{ fontSize: 14, color: "#64748b", marginLeft: 6 }}>hours this week</span>
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>across {entries.length} entr{entries.length === 1 ? "y" : "ies"}</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => exportCsv(entries, `timesheet-${weekLabel}.csv`)}
              style={{ background: "white", border: "1px solid #bfdbfe", borderRadius: 7, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: NAVY, cursor: "pointer" }}>
              ⬇ Export CSV
            </button>
            {isContractor && !weekLocked && totalHours > 0 && (
              <button onClick={() => setShowSubmitForm(v => !v)}
                style={{ background: NAVY, color: "white", border: "none", borderRadius: 7, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Submit for Approval
              </button>
            )}
          </div>
        </div>
      )}

      {/* Submit form */}
      {showSubmitForm && (
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px", marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: NAVY, marginBottom: 8 }}>Submit week of {startStr} for approval</div>
          <textarea value={submitNotes} onChange={e => setSubmitNotes(e.target.value)}
            placeholder="Optional notes for your manager…"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, resize: "vertical", height: 60, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={() => submitMut.mutate()} disabled={submitMut.isPending}
              style={{ background: NAVY, color: "white", border: "none", borderRadius: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {submitMut.isPending ? "Submitting…" : "Submit Week"}
            </button>
            <button onClick={() => setShowSubmitForm(false)}
              style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 14px", fontSize: 13, cursor: "pointer", color: "#64748b" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {weekDates.map((date, idx) => {
          const dayEntries = byDate[date] || [];
          const dayTotal = dayEntries.reduce((s: number, e: any) => s + (e.hours || 0), 0);
          const isToday = date === toIso(new Date());
          return (
            <div key={date} style={{ background: "white", border: `1px solid ${isToday ? "#bfdbfe" : "#e2e8f0"}`, borderRadius: 10, padding: "14px 18px", boxShadow: isToday ? "0 0 0 2px #eff6ff" : undefined }}>
              <div className="card-split-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: dayEntries.length > 0 ? 10 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: isToday ? NAVY : "#334155" }}>{DAYS[idx]}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>{new Date(date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  {isToday && <span style={{ fontSize: 10, background: "#dbeafe", color: NAVY, borderRadius: 10, padding: "1px 8px", fontWeight: 600 }}>Today</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {dayTotal > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: TEAL }}>{dayTotal}h</span>}
                  {!weekLocked && <AddEntryRow date={date} onSave={() => {}} />}
                </div>
              </div>
              {dayEntries.length > 0 && (
                <div style={{ display: "grid", gap: 5 }}>
                  {dayEntries.map((e: any) => (
                    <EntryChip key={e.id} entry={e}
                      onDelete={() => deleteMut.mutate(e.id)}
                      onEdit={weekLocked ? () => {} : setEditEntry}
                      deleteDisabled={deleteMut.isPending || !!weekLocked}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Team Timesheet tab ────────────────────────────────────────────────────────
function TeamTimesheetTab({ weekStart, weekLabel }: { weekStart: Date; weekLabel: string }) {
  const weekDates = DAYS.map((_, i) => toIso(addDays(weekStart, i)));
  const [filterUserId, setFilterUserId] = useState<number | undefined>();

  const { data: employees = [] } = useQuery<any[]>({ queryKey: ["employees"], queryFn: getEmployees });

  const startStr = weekDates[0];
  const endStr = weekDates[6];

  const { data: entries = [], isLoading } = useQuery<any[]>({
    queryKey: ["timesheet-team", startStr, endStr, filterUserId],
    queryFn: () => getTeamTimesheetEntries({ start_date: startStr, end_date: endStr, user_id: filterUserId }),
  });

  // Group by employee
  const byEmployee: Record<number, { name: string; entries: any[] }> = {};
  entries.forEach((e: any) => {
    if (!byEmployee[e.user_id]) byEmployee[e.user_id] = { name: e.employee_name, entries: [] };
    byEmployee[e.user_id].entries.push(e);
  });

  const totalHours = entries.reduce((s: number, e: any) => s + (e.hours || 0), 0);

  return (
    <>
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={lbl}>Filter by Employee</label>
          <select value={filterUserId ?? ""} onChange={e => setFilterUserId(e.target.value ? parseInt(e.target.value) : undefined)}
            style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, width: "100%", maxWidth: 220, boxSizing: "border-box" }}>
            <option value="">All employees</option>
            {(employees as any[]).map((emp: any) => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        </div>
        {totalHours > 0 && (
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 16px", fontSize: 14 }}>
            <strong style={{ color: NAVY }}>{totalHours}h</strong> <span style={{ color: "#64748b" }}>total this week</span>
          </div>
        )}
        {entries.length > 0 && (
          <button
            onClick={() => exportCsv(entries, `team-timesheet-${weekLabel}.csv`)}
            style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: NAVY, cursor: "pointer" }}>
            ⬇ Export CSV
          </button>
        )}
      </div>

      {isLoading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : Object.keys(byEmployee).length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>No entries logged this week.</div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {Object.entries(byEmployee).map(([uid, { name, entries: empEntries }]) => {
            const empTotal = empEntries.reduce((s, e) => s + (e.hours || 0), 0);
            const byDate: Record<string, any[]> = {};
            empEntries.forEach(e => { if (!byDate[e.date]) byDate[e.date] = []; byDate[e.date].push(e); });

            return (
              <div key={uid} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                <div className="card-split-row" style={{ background: "#f8fafc", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0" }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{name}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>{empTotal}h</span>
                </div>
                <div style={{ padding: "12px 18px", display: "grid", gap: 6 }}>
                  {Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, dayEntries]) => (
                    <div key={date}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", marginBottom: 4 }}>
                        {new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </div>
                      <div style={{ display: "grid", gap: 4 }}>
                        {dayEntries.map((e: any) => (
                          <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 0" }}>
                            <span style={{ fontWeight: 700, color: NAVY, minWidth: 32 }}>{e.hours}h</span>
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>{e.project_name}</span>
                            {e.client_name && <span style={{ color: "#64748b" }}>· {e.client_name}</span>}
                            {e.notes && <span style={{ color: "#94a3b8" }}>— {e.notes}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Approvals tab (manager) ───────────────────────────────────────────────────
function ApprovalsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data: pending = [], isLoading } = useQuery<any[]>({
    queryKey: ["timesheet-approvals"],
    queryFn: getTimesheetApprovals,
  });

  const approveMut = useMutation({
    mutationFn: (id: number) => approveTimesheetWeek(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["timesheet-approvals"] }); toast("Approved", "success"); },
    onError: () => toast("Failed to approve", "error"),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectTimesheetWeek(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["timesheet-approvals"] }); setRejectId(null); setRejectReason(""); toast("Returned for revision", "success"); },
    onError: () => toast("Failed to reject", "error"),
  });

  if (isLoading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>;
  if ((pending as any[]).length === 0) return (
    <div style={{ padding: 60, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>No pending approvals.</div>
  );

  return (
    <>
      <LoadingOverlay active={approveMut.isPending || rejectMut.isPending} />
      {rejectId !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "white", borderRadius: 12, padding: 24, width: 400, maxWidth: "92vw" }}>
            <div style={{ fontWeight: 700, color: NAVY, marginBottom: 12 }}>Return for Revision</div>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason (optional)…" rows={3}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => rejectMut.mutate({ id: rejectId!, reason: rejectReason })} disabled={rejectMut.isPending}
                style={{ background: "#dc2626", color: "white", border: "none", borderRadius: 7, padding: "8px 18px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                {rejectMut.isPending ? "…" : "Return"}
              </button>
              <button onClick={() => { setRejectId(null); setRejectReason(""); }}
                style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 14px", cursor: "pointer", fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "grid", gap: 12 }}>
        {(pending as any[]).map((w: any) => (
          <div key={w.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>{w.employee_name}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Week of {w.week_start} · <strong style={{ color: NAVY }}>{w.total_hours}h</strong></div>
              {w.notes && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>"{w.notes}"</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => approveMut.mutate(w.id)} disabled={approveMut.isPending}
                style={{ background: "#16a34a", color: "white", border: "none", borderRadius: 7, padding: "7px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                ✓ Approve
              </button>
              <button onClick={() => setRejectId(w.id)}
                style={{ background: "white", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 7, padding: "7px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Return
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TimesheetPage() {
  const { user } = useAuth();
  const isManager = ["admin", "admin_assistant", "hr"].includes(user?.role ?? "");
  const isContractor = user?.role === "contractor";
  const [tab, setTab] = useState<"my" | "team" | "approvals">("my");
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = getWeekStart(addDays(new Date(), weekOffset * 7));
  const weekLabel = toIso(weekStart);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Timesheet</h1>
          <p>Log hours by project for the week</p>
        </div>
      </div>

      {/* Week navigator */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <button onClick={() => setWeekOffset(w => w - 1)}
          style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 7, padding: "7px 14px", cursor: "pointer", fontSize: 15, color: "#475569" }}>←</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, minWidth: 220, textAlign: "center" }}>{fmtWeek(weekStart)}</div>
        <button onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0}
          style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 7, padding: "7px 14px", cursor: weekOffset >= 0 ? "not-allowed" : "pointer", fontSize: 15, color: weekOffset >= 0 ? "#cbd5e1" : "#475569" }}>→</button>
        {weekOffset !== 0 && (
          <button onClick={() => setWeekOffset(0)}
            style={{ fontSize: 13, color: NAVY, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>This week</button>
        )}
      </div>

      {/* Tab bar */}
      {(isManager || isContractor) && (
        <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
          <button onClick={() => setTab("my")} className={tab === "my" ? "active" : ""}>My Timesheet</button>
          {isManager && <button onClick={() => setTab("team")} className={tab === "team" ? "active" : ""}>Team Timesheet</button>}
          {isManager && <button onClick={() => setTab("approvals")} className={tab === "approvals" ? "active" : ""}>Approvals</button>}
        </div>
      )}

      {tab === "my" && <MyTimesheetTab weekStart={weekStart} weekLabel={weekLabel} />}
      {tab === "team" && isManager && <TeamTimesheetTab weekStart={weekStart} weekLabel={weekLabel} />}
      {tab === "approvals" && isManager && <ApprovalsTab />}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const inSm: React.CSSProperties = {
  padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 12, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
const inFull: React.CSSProperties = {
  width: "100%", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 7,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
const smBtn: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", borderRadius: 6,
  padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#475569", flexShrink: 0,
};
const lbl: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
